<template>
  <SectionAccordion04
    :title="t('title')"
    :description-html="highlight('description')"
    :items="items"
    theme="cyan"
  />
</template>

<script setup lang="ts">
  import type {
    IItem,
    ISubItem,
  } from '@/components/Shared/Section/Accordion/04.vue';

  const { t, locale } = useI18n({ useScope: 'local' });
  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const highlight = (key: string) =>
    t(key).replace(
      /\[\[(.+?)\]\]/g,
      '<span class="font-bold text-brand-400">$1</span>'
    );

  const provideSubItem = (
    key: string,
    path: string,
    thumbnail: string
  ): ISubItem => ({
    name: t(`${key}-name`),
    link: `/${locale.value}/marketplace${path}`,
    thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics2/doe/${provideResponsiveImgName(thumbnail)}`,
    features: [1, 2, 3].map((index) => t(`${key}-feature-${index}`)),
  });

  const items = computed((): IItem[] => [
    {
      name: t('item-1-name'),
      description: t('item-1-description'),
      items: [
        provideSubItem(
          'item-1-sub-1',
          '/products/advantech.edgehub',
          'journey-1'
        ),
      ],
    },
    {
      name: t('item-2-name'),
      description: t('item-2-description'),
      items: [
        provideSubItem(
          'item-2-sub-1',
          '/solutions/wise-iotsuite',
          'journey-2-1'
        ),
        provideSubItem(
          'item-2-sub-2',
          '/products/advantech.iems-ecowatch',
          'journey-2-2'
        ),
      ],
    },
    {
      name: t('item-3-name'),
      description: t('item-3-description'),
      items: [
        provideSubItem(
          'item-3-sub-1',
          '/products/advantech.phm',
          'journey-3-1'
        ),
        provideSubItem(
          'item-3-sub-2',
          '/help/sales-support?IssueType=1',
          'journey-3-2'
        ),
      ],
    },
  ]);
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "en-jp": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "en-tw": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "en-kr": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "en-americas": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "en-asia-pacific": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "en-emea": {
    "title": "Digital O&M Journey",
    "description": "A clear [[three-stage maturity path]] that connects assets, standardizes operations, and enables AI-driven intelligence —laying the foundation for [[scalable, resilient]] digital O&M.",
    "item-1-name": "Edge Connectivity & Management",
    "item-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "Plug-and-play onboarding & updates",
    "item-1-sub-1-feature-2": "Instant app deployment across devices",
    "item-1-sub-1-feature-3": "Secure PLC access with encrypted tunnels",
    "item-2-name": "Remote Multi-Site Operations",
    "item-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "Unify and analyze multi-source, heterogeneous data",
    "item-2-sub-1-feature-2": "Monitor and manage system anomalies in real time",
    "item-2-sub-1-feature-3": "Build low-code apps on an open platform",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "Track and benchmark energy consumption",
    "item-2-sub-2-feature-2": "Detect anomalies and energy exceptions",
    "item-2-sub-2-feature-3": "Multi-dimensional insights for energy strategies",
    "item-3-name": "AI Optimization",
    "item-3-description": "Advance from monitoring to optimization with AI insights, forecasting, and decision support for reliable, sustainable operations",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "Predictive maintenance & early anomaly detection",
    "item-3-sub-1-feature-2": "Estimate remaining useful life (RUL)",
    "item-3-sub-1-feature-3": "Identify failure patterns across assets",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "Load forecasting and demand prediction",
    "item-3-sub-2-feature-2": "Energy cost optimization advisory",
    "item-3-sub-2-feature-3": "AI guidance for peak shaving & load shifting"
  },
  "ja-jp": {
    "title": "デジタルO&Mの導入ステージ",
    "description": "資産をつなぎ、運用を標準化し、AIドリブンのインテリジェンスを実現する、明確な[[3段階の成熟化ロードマップ]]。[[拡張性とレジリエンスを備え]]たデジタルO&Mの基盤を構築します。",
    "item-1-name": "エッジ接続とデバイス管理",
    "item-1-description": "組み込み型の遠隔管理機能と継続的なデバイス可視化により、安全で常時稼働する運用環境を実現します。",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "迅速な導入とデバイス更新",
    "item-1-sub-1-feature-2": "エッジへのアプリ即時展開",
    "item-1-sub-1-feature-3": "暗号化トンネルでPLC接続",
    "item-2-name": "遠隔マルチサイト運用",
    "item-2-description": "各拠点の可視性を集約し、運用の標準化を推進するとともに、エネルギー性能のベンチマークを実現します。",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "異種データの統合・分析",
    "item-2-sub-1-feature-2": "システム横断の異常監視",
    "item-2-sub-1-feature-3": "ローコードアプリ開発・連携",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "エネルギー使用量を把握・比較",
    "item-2-sub-2-feature-2": "異常や想定外のエネルギー使用を迅速に検知",
    "item-2-sub-2-feature-3": "多角的分析で省エネ施策を最適化",
    "item-3-name": "AI最適化",
    "item-3-description": "AIによるインサイト、予測分析、意思決定支援を組み合わせ、運用状況のリアルタイム把握から予測・最適化へと発展させ、安定性とサステナビリティの向上を支援します。",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "設備異常を早期に検知し、予知保全を実現",
    "item-3-sub-1-feature-2": "設備の残存寿命（RUL）を予測",
    "item-3-sub-1-feature-3": "複数の設備に共通する故障パターンを分析・特定",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "電力負荷とエネルギー需要を予測",
    "item-3-sub-2-feature-2": "エネルギーコスト最適化を提案",
    "item-3-sub-2-feature-3": "ピークカット・負荷シフトを提案"
  },
  "zh-tw": {
    "title": "數位維運旅程",
    "description": "透過[[三階段的發展路徑]]，協助企業逐步完成資產串聯、營運標準化與 AI 智慧化，為[[可擴展且具韌性]]的數位維運奠定基礎。",
    "item-1-name": "邊緣連線與設備管理",
    "item-1-description": "透過內建遠端管理與持續設備監控，確保安全且穩定的營運。",
    "item-1-sub-1-name": "EdgeHub",
    "item-1-sub-1-feature-1": "裝置即插即用，快速上線並支援大規模更新",
    "item-1-sub-1-feature-2": "應用程式可即時部署至各邊緣裝置",
    "item-1-sub-1-feature-3": "加密通道確保 PLC 安全連線",
    "item-2-name": "遠端多據點維運管理",
    "item-2-description": "集中管理各據點的可視性、標準化維運流程，並對能源績效進行系統化的基準評比",
    "item-2-sub-1-name": "IoTSuite",
    "item-2-sub-1-feature-1": "整合分析多來源異質資料",
    "item-2-sub-1-feature-2": "即時監控跨系統異常",
    "item-2-sub-1-feature-3": "串接開放式平台，加速低程式碼應用開發",
    "item-2-sub-2-name": "ECOWatch",
    "item-2-sub-2-feature-1": "持續追蹤能源使用，建立能耗比較基準",
    "item-2-sub-2-feature-2": "即時辨識異常與非預期用能狀況",
    "item-2-sub-2-feature-3": "多面向分析能源數據，優化節能策略",
    "item-3-name": "AI 智慧優化",
    "item-3-description": "結合 AI 洞察、預測分析與決策支援，從即時掌握營運狀況，進一步做到預先判斷與優化，提升營運穩定性與永續表現。",
    "item-3-sub-1-name": "PHM",
    "item-3-sub-1-feature-1": "提前掌握設備異常，實現預測性維護",
    "item-3-sub-1-feature-2": "預估設備剩餘使用壽命（RUL）",
    "item-3-sub-1-feature-3": "分析跨設備的故障狀態與問題",
    "item-3-sub-2-name": "iEMS.AI Agent",
    "item-3-sub-2-feature-1": "預測用能負載與能源需求",
    "item-3-sub-2-feature-2": "分析用能狀況，提供能源成本最佳化建議",
    "item-3-sub-2-feature-3": "運用 AI 提出削峰與負載移轉策略"
  }
}
</i18n>
